/**
 * A relationship is ADDITIVE on inheritance: a subtype gets every ancestor's declaration and
 * can never remove one. What it CAN do is tighten its own view of one, and only in the
 * direction that keeps the ancestor's promise true:
 *
 *   rename       name / description / reverse label — presentation, free
 *   narrow       drop inherited targets, or AND extra conditions onto the rule
 *   strengthen   optional → required, and a "many" end → "one"
 *
 * Never: remove, widen, or change kind. A reader that trusts the ancestor's contract would
 * stop being able to read the subtype, so those are not offered at all.
 *
 * This module is the ONE derivation of "the row as this type sees it" — read by the override
 * sheet, by the adjusted-count on the relationship row, and by the sheet's own summary.
 */
import type { SchemaRelationship } from 'types/schema';
import type { FilterExpression, UnifiedFilters } from 'types/filters';

/** What a subtype adjusted, as translation-key suffixes rather than user-facing words. */
export type RelationshipAdjustment =
  | 'name'
  | 'description'
  | 'reverseLabel'
  | 'required'
  | 'howMany'
  | 'targets';

/** The subtype's adjustments to one inherited relationship. Every field is optional. */
export interface RelationshipOverridePatch {
  name?: string | null;
  description?: string | null;
  reverse_label?: string | null;
  cardinality?: SchemaRelationship['cardinality'] | null;
  required?: boolean;
  /** Target ids kept from the ancestor's list. `null`/absent = every one of them. */
  keep?: string[] | null;
  /** The rule authored when the ancestor allows any sphere — there is nothing to narrow. */
  scope?: UnifiedFilters | null;
  /** Conditions ANDed onto the inherited rule, so they can only narrow it further. */
  extraFilters?: FilterExpression[];
}

export interface RelationshipEffective {
  name: string;
  description: string;
  reverseLabel: string;
  cardinality: SchemaRelationship['cardinality'];
  required: boolean;
  /** The ancestor's target ids this subtype still allows. */
  kept: string[];
  extraFilters: FilterExpression[];
  adjusted: RelationshipAdjustment[];
}

/**
 * `[source, target]` — `1` means "many". A side may be tightened (1 → 0), never loosened, so
 * a subtype can turn a "many" end into a "one" but never the reverse.
 */
const CARD_SIDES: Record<SchemaRelationship['cardinality'], [number, number]> = {
  one_to_one: [0, 0],
  many_to_one: [1, 0],
  one_to_many: [0, 1],
  many_to_many: [1, 1],
};

/** Whether a subtype may move from `base` to `candidate` without widening either end. */
export function relCardAllowed(
  base: SchemaRelationship['cardinality'],
  candidate: SchemaRelationship['cardinality']
): boolean {
  const b = CARD_SIDES[base] ?? [1, 1];
  const c = CARD_SIDES[candidate] ?? [1, 1];
  return c[0] <= b[0] && c[1] <= b[1];
}

/**
 * A condition counts as a narrowing only once it names a property — a half-authored row
 * narrows nothing, and counting it would report the relationship as adjusted before the
 * author has said what by.
 */
const isAuthored = (f: FilterExpression) => !!f?.property;

/**
 * The relationship as `typeName` sees it: the ancestor's declaration with the subtype's
 * adjustments overlaid, plus the list of what was adjusted.
 *
 * `baseTargetIds` is the ancestor's target list (families + types); an empty list means the
 * ancestor allows any sphere, which is the one case where `scope` — a rule authored from
 * scratch rather than a narrowing of a list — applies.
 */
export function relEffective(
  base: SchemaRelationship,
  override: RelationshipOverridePatch | null | undefined,
  baseTargetIds: string[] = []
): RelationshipEffective {
  const baseName = base.name ?? '';
  const baseDescription = base.description ?? '';
  const baseReverse = base.reverse_label ?? '';
  const empty: RelationshipEffective = {
    name: baseName,
    description: baseDescription,
    reverseLabel: baseReverse,
    cardinality: base.cardinality,
    required: !!base.required,
    kept: baseTargetIds,
    extraFilters: [],
    adjusted: [],
  };
  if (!override) return empty;

  const adjusted: RelationshipAdjustment[] = [];
  const out = { ...empty };

  if (override.name && override.name !== baseName) {
    out.name = override.name;
    adjusted.push('name');
  }
  if (override.description && override.description !== baseDescription) {
    out.description = override.description;
    adjusted.push('description');
  }
  if (override.reverse_label && override.reverse_label !== baseReverse) {
    out.reverseLabel = override.reverse_label;
    adjusted.push('reverseLabel');
  }
  if (override.required && !base.required) {
    out.required = true;
    adjusted.push('required');
  }
  if (override.cardinality && override.cardinality !== base.cardinality) {
    out.cardinality = override.cardinality;
    adjusted.push('howMany');
  }

  const kept = override.keep?.length
    ? baseTargetIds.filter((id) => override.keep?.includes(id))
    : baseTargetIds;
  out.kept = kept;
  if (kept.length !== baseTargetIds.length) adjusted.push('targets');
  if (override.scope && baseTargetIds.length === 0) adjusted.push('targets');

  out.extraFilters = override.extraFilters ?? [];
  if (out.extraFilters.some(isAuthored) && !adjusted.includes('targets')) adjusted.push('targets');

  out.adjusted = adjusted;
  return out;
}

/** How many things this subtype adjusted — what the relationship row's chip counts. */
export function relOverrideCount(
  base: SchemaRelationship,
  override: RelationshipOverridePatch | null | undefined,
  baseTargetIds: string[] = []
): number {
  return relEffective(base, override, baseTargetIds).adjusted.length;
}

/* ---------------------------------------------------------------------------
 * The two directions between a patch and a stored declaration.
 *
 * A subtype ADJUSTS an inherited relationship by re-declaring the same
 * `system_name` in its own `relationships[]`; `fetchAndMergeDefinitions` collapses
 * the two by `system_name` (child wins) and hands the row back with
 * `adjusted_from` — the ancestor's declaration as it stands. So:
 *
 *   relApplyOverride   patch  → the declaration to store   (the sheet's save)
 *   relAdjustments     stored → what was adjusted          (the row's chip)
 *
 * There is no separate override store. A patch that lived beside the array would
 * be invisible to graph-service's target validation, which reads `target_filter`
 * off the merged relationship — the narrowing would be cosmetic and unenforced.
 * ------------------------------------------------------------------------ */

/** The property axes `buildApplicability` writes a specific-type list onto. */
const TARGET_LIST_PROPERTIES = ['label', 'type'];

/**
 * The relationship this type stores, given the ancestor's declaration and the sheet's
 * patch. The base's `target_filter` is transformed in place rather than rebuilt from
 * the applicability model: `buildApplicability` writes a type list OR conditions, so
 * rebuilding would drop whichever the ancestor did not use, and every lane the
 * ancestor authored that neither this sheet nor that model can express.
 */
export function relApplyOverride(
  base: SchemaRelationship,
  override: RelationshipOverridePatch | null | undefined,
  baseTargetIds: string[] = []
): SchemaRelationship {
  const eff = relEffective(base, override, baseTargetIds);
  const authoredExtras = eff.extraFilters.filter(isAuthored);

  let targetFilter: UnifiedFilters = base.target_filter ?? {};
  if (override?.scope && baseTargetIds.length === 0) {
    // The ancestor allows any sphere: there is nothing to narrow, so the rule the
    // subtype authored from scratch IS the target filter.
    targetFilter = override.scope;
  } else if (eff.kept.length !== baseTargetIds.length) {
    targetFilter = {
      ...targetFilter,
      propertyFilters: (targetFilter.propertyFilters ?? []).map((f) =>
        TARGET_LIST_PROPERTIES.includes(f.property)
          ? { ...f, values: (f.values ?? []).filter((v) => eff.kept.includes(String(v))) }
          : f
      ),
    };
  }
  if (authoredExtras.length) {
    // ANDed onto the inherited rule — `propertyFilters` is a conjunction, so extras
    // can only ever narrow it further, which is the one direction a subtype may move.
    targetFilter = {
      ...targetFilter,
      propertyFilters: [...(targetFilter.propertyFilters ?? []), ...authoredExtras],
    };
  }

  return {
    ...base,
    name: eff.name,
    label: eff.name,
    description: eff.description,
    reverse_label: eff.reverseLabel,
    cardinality: eff.cardinality,
    required: eff.required,
    target_filter: targetFilter,
  };
}

/**
 * What this type adjusted, read off the merged row rather than off a patch: the
 * declaration that won the collapse, diffed against the `adjusted_from` base the
 * merge stamped on it. An unadjusted inherited row has no base and returns `[]`.
 *
 * `targets` is a structural comparison of the whole `target_filter` — a subtype can
 * narrow by dropping ids, by ANDing a condition, or (where the ancestor allowed any
 * sphere) by authoring a rule outright, and the chip counts all three as one change
 * to what the relationship points at, exactly as the sheet's summary does.
 */
export function relAdjustments(
  effective: SchemaRelationship,
  base: SchemaRelationship | null | undefined = effective.adjusted_from
): RelationshipAdjustment[] {
  if (!base) return [];
  const adjusted: RelationshipAdjustment[] = [];
  if ((effective.name ?? '') !== (base.name ?? '')) adjusted.push('name');
  if ((effective.description ?? '') !== (base.description ?? '')) adjusted.push('description');
  if ((effective.reverse_label ?? '') !== (base.reverse_label ?? '')) adjusted.push('reverseLabel');
  if (!!effective.required !== !!base.required) adjusted.push('required');
  if (effective.cardinality !== base.cardinality) adjusted.push('howMany');
  if (JSON.stringify(effective.target_filter ?? {}) !== JSON.stringify(base.target_filter ?? {})) {
    adjusted.push('targets');
  }
  return adjusted;
}
